/**
 * Shared 3-step stepper used by welcome / first_deposit / reload / cashback.
 * Renders three numbered nodes connected by lines; each node is either done
 * (green check), active (yellow with pulse), or pending (gray).
 */
interface Step {
  label: string;
}
interface Props {
  steps: [Step, Step, Step];
  /** 1..3 = step in progress; 4 = all done (claim completed). */
  currentStep: 1 | 2 | 3 | 4;
  /** When step 3 is active, render this progress bar (turnover 0..1). */
  progress?: number;
  /** Pulse the active step. Default true. */
  pulse?: boolean;
  themeColor?: string;
}

export function ThreeStepStepper({
  steps,
  currentStep,
  progress,
  pulse = true,
  themeColor = 'var(--primary)',
}: Props) {
  return (
    <div className="flex flex-col gap-2">
      {steps.map((s, i) => {
        const n = (i + 1) as 1 | 2 | 3;
        const state = n < currentStep ? 'done' : n === currentStep ? 'active' : 'pending';
        const dotClass =
          state === 'done'
            ? 'bg-[var(--success)] text-white'
            : state === 'active'
              ? `bg-[#fbbf24] text-black ${pulse ? 'animate-pulse' : ''}`
              : 'bg-[var(--bg-elevated)] text-[var(--text-muted)]';
        const textClass =
          state === 'done'
            ? 'text-[var(--success)]'
            : state === 'active'
              ? 'text-[var(--text-primary)]'
              : 'text-[var(--text-muted)]';
        return (
          <div key={i} className="flex items-center gap-2.5">
            <div
              className={`w-[22px] h-[22px] rounded-full flex items-center justify-center font-bold text-[10px] ${dotClass}`}
            >
              {state === 'done' ? '✓' : n}
            </div>
            <div className={`text-[11px] ${textClass}`}>{s.label}</div>
          </div>
        );
      })}
      {currentStep === 3 && progress != null && (
        <div className="bg-[var(--bg-base)] h-[6px] rounded-full overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${Math.min(100, Math.max(0, progress * 100))}%`,
              background: themeColor,
            }}
          />
        </div>
      )}
    </div>
  );
}
